import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'

/**
 * REST client for PDF report generation via jsReport.
 *
 * Sends a document data payload directly to a jsReport server instance
 * and receives a PDF `Blob` in response. The report server URL and
 * authentication key are embedded in the `invoiceData` payload by the caller —
 * this service does not resolve them from global settings.
 *
 * @remarks
 * Unlike other API services, this service does NOT send Webyto connection
 * headers. Requests go directly to the jsReport server, not to the backend
 * Express API. The jsReport endpoint and `Authorization` header must be
 * included in `invoiceData` by the caller.
 *
 * The response type is `blob` so Angular deserialises the binary PDF response
 * correctly. The `retry(0)` is intentional — retrying a report generation
 * request risks double-rendering or duplicate file saves on the report server.
 */
@Injectable({
  providedIn: 'root'
})
export class ReportApiService {
  apiURL: string = '';

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) { }

  // Http Options
  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    responseType: 'blob' as 'json'
  };

/*
 jsReport request body shape:
 {
  "template": { "name" : "my template" },
  "data" : { ... },
  "options": { "reports": { "save": true } }
 }
*/

  /**
   * Submits a document data payload to the jsReport server and retrieves the
   * rendered PDF as a `Blob`.
   *
   * The target report template, jsReport server URL (`reportServerURI`), and
   * any authentication credentials are resolved by the caller and embedded
   * in `invoiceData`. This method is a thin HTTP POST wrapper with binary
   * response handling.
   *
   * @param invoiceData - Payload containing the jsReport template reference,
   *   report data, and `reportServerURI` (the full jsReport API endpoint URL).
   * @returns Observable emitting the rendered PDF as a `Blob`.
   */
  getInvoiceForm(invoiceData: any): Observable<Blob> {
    let jsReportHttpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        //'Authorization': 'Basic ' + invoiceData.AuthKeyStr
      }),
      responseType: 'blob' as 'json'
    };
    return this.http.post<Blob>(invoiceData.reportServerURI, invoiceData, jsReportHttpOptions)
      .pipe(
        retry(0),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Error handling
  // ---------------------------------------------------------------------------

  /**
   * Centralised HTTP error handler used by all methods in this service.
   *
   * Extracts a human-readable message, shows a browser `alert` (legacy pattern
   * — Phase 1 will replace with a toast), and re-throws so the calling
   * Observable terminates.
   *
   * @param error - Angular `HttpErrorResponse` or `ErrorEvent`.
   * @returns An Observable that immediately errors with the formatted message.
   */
  handleError(error) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    window.alert(errorMessage);
    return throwError(() => errorMessage);
  }
}
